import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useBranding, useUpsertBrandingText } from "@/hooks/useBranding";
import type { BrandingTextKey } from "@/lib/branding-keys";

interface Props {
  brandingKey: BrandingTextKey;
  label: string;
  description?: string;
  previewBg?: "light" | "dark";
  accept?: string;
  /** Max upload size in bytes. Default 2 MB. */
  maxSize?: number;
  /** Invoked with the resulting public URL after successful upload + save. */
  onUploaded?: (publicUrl: string) => void | Promise<void>;
}

const DEFAULT_ACCEPT = "image/png,image/svg+xml,image/webp,image/jpeg";

export function LogoUploader({
  brandingKey,
  label,
  description,
  previewBg = "light",
  accept = DEFAULT_ACCEPT,
  maxSize = 2 * 1024 * 1024,
  onUploaded,
}: Props) {
  const { getText } = useBranding();
  const upsert = useUpsertBrandingText();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const currentUrl = getText(brandingKey);

  async function handleFile(file: File) {
    if (!file) return;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${(maxSize / 1024 / 1024).toFixed(1)} MB.`);
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "png";
      // Stable path (overwrite-on-upload) so old URLs don't accumulate.
      // Add a timestamp in a querystring (below) to bust caches without losing
      // the stable storage path.
      const path = `${brandingKey.replace(/\./g, "-")}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("branding")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("branding").getPublicUrl(path);
      // Cache-bust by appending a ts param; SPA and TC read the full URL.
      const busted = `${data.publicUrl}?v=${Date.now()}`;

      await new Promise<void>((resolve, reject) => {
        upsert.mutate(
          { key: brandingKey, value: busted },
          { onSuccess: () => resolve(), onError: (e) => reject(e) }
        );
      });

      toast.success(`${label} uploaded`);
      if (onUploaded) await onUploaded(busted);
    } catch (e) {
      toast.error((e as Error).message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function clear() {
    await new Promise<void>((resolve, reject) => {
      upsert.mutate(
        { key: brandingKey, value: "" },
        { onSuccess: () => resolve(), onError: (e) => reject(e) }
      );
    });
    toast.success(`${label} cleared`);
  }

  const bgClass = previewBg === "dark" ? "bg-zinc-900" : "bg-muted/40";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        {currentUrl && (
          <Button variant="ghost" size="sm" onClick={clear} disabled={upsert.isPending}>
            <X className="h-3.5 w-3.5 mr-1" /> Remove
          </Button>
        )}
      </div>

      <div className={`flex items-center justify-center rounded-lg border border-dashed border-border/60 p-6 h-32 ${bgClass}`}>
        {currentUrl ? (
          <img src={currentUrl} alt={label} className="max-h-20 max-w-full object-contain" />
        ) : (
          <div className="text-center text-xs text-muted-foreground">
            <AlertTriangle className="h-4 w-4 mx-auto mb-1 opacity-50" />
            No file uploaded
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        {currentUrl ? "Replace" : "Upload"}
      </Button>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}
